import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isPaymentInReview, paymentInReviewMessage, paymentErrorMessage } from '../paymentErrors';
import axios from 'axios';
import { DateTime } from 'luxon';
import Select from 'react-select';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faHourglassHalf, faInfoCircle, faCheckCircle, faWandMagicSparkles, faShieldHalved, faBuilding, faPlus } from '@fortawesome/free-solid-svg-icons';
import ClientDepositForm from '../ClientWallet/ClientDepositForm';
import EmptyState from '../EmptyState';
import IncludedSet from './IncludedSet';
import SavedCardPicker, { cardCheckoutBody } from '../ClientWallet/SavedCardPicker';
import HostedCardPanel from '../payments/HostedCardPanel';
import SavedCardPay from '../payments/SavedCardPay';
import { CARD_CONSENT_TEXT, CARD_CONSENT_VERSION } from '../ClientWallet/cardConsent';
import {
    AUTOPAY_SETUP_AMOUNT_LABEL,
    AUTOPAY_SETUP_BODY,
    AUTOPAY_SETUP_BUTTON,
    AUTOPAY_SETUP_ROW_BOOKING,
    AUTOPAY_SETUP_ROW_VERIFICATION,
    autoPaySetupHeadline,
    autoPaySetupPendingNote,
} from '../ClientWallet/autoPaySetupCopy';
import { SchemeBadge, cardLast4 } from '../ClientWallet/savedCardUtils';
import {
    AUTO_CHARGE_UI_ENABLED,
    AUTO_PAY_BLURB,
    AUTO_PAY_INVITE,
    readAutoPayEnabled,
    reevaluatePayMode,
    saveAutoPayEnabled,
} from '../ClientWallet/autoPay';
import { coralSelectStyles, coralSelectTheme } from './coralSelect';
import TaskListEditor from './TaskListEditor';
import BookingDateStrip from './BookingDateStrip';
import { saveBookingDraft, clearBookingDraft, loadBookingDraft } from './bookingRequestDraft';
import { setTopupBookingIntent } from './topupBookingIntent';
import { resolvePayNow, payNowRailLabel, combinedCheckoutFigures } from '../payNow';
import WalletShortfallPanel from '../WalletShortfallPanel';
import { readWalletFloor } from '../walletShortfall';
import useBfcacheReset from '../useBfcacheReset';
import './clientRequests.css';

// Clients can only book All Inclusive (2026-07-06) — kept visible &
// pre-selected so the service is clear; hourly options removed.
const SERVICE_TYPES = [
    { id: 1, name: 'All Inclusive', desc: 'Full cleaning with linen & amenities at your fixed property price' },
];

const HOURLY_TYPE_IDS = [2, 4];
const DURATION_OPTIONS = [2, 2.5, 3, 3.5, 4, 4.5, 5, 5.5, 6, 6.5, 7, 7.5, 8]
    .map((h) => ({ value: h, label: `${h} Hour${h === 1 ? '' : 's'}` }));

// Selectable start times: 10:00 → 17:00 in 30-minute steps, EXCLUDING 15:00 and
// 15:30 — the window just before the 16:00 off-peak discount. Removed so clients
// aren't confused about whether the 10% All-Inclusive discount applies.
const EXCLUDED_SLOTS = ['15:00', '15:30'];
const ALL_TIME_SLOTS = [];
for (let h = 10; h <= 16; h++) {
    for (const m of ['00', '30']) {
        const slot = `${String(h).padStart(2, '0')}:${m}`;
        if (!EXCLUDED_SLOTS.includes(slot)) ALL_TIME_SLOTS.push(slot);
    }
}
ALL_TIME_SLOTS.push('17:00');

// All-Inclusive (type_id 1) is priced OFF-PEAK (−10%) from 16:00 onward.
const OFFPEAK_FROM = '16:00';

// Standing client explanation for the one-time linen deposit (fallback if the
// estimate response doesn't carry its own note).
const LINEN_DEPOSIT_NOTE = 'A one-time linen deposit of AED 100 per bed is added before the first cleaning at this property. It is fully refundable when you stop using our services, provided all linens are returned in good condition.';

// Linen-hire inclusion window (2026-07-14): all-inclusive cleanings include
// linen hire for up to two weeks; kept longer it's chargeable every two weeks.
// Types match linenDepositService.LINEN_DEPOSIT_BOOKING_TYPES.
const LINEN_HIRE_TYPES = [1, 5];
const LINEN_HIRE_NOTE = 'Bookings include linen hire for up to 2 weeks. Linens kept longer are chargeable, billed every 2 weeks.';

const fmtNum = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const money = (n) => `AED ${fmtNum(n)}`;

function parseLocalDate(ymd) {
    if (!ymd) return null;
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(y, m - 1, d);
}
// ITEM-8 EXCEPTION (deliberate — do NOT route through dubaiTime): this labels
// a calendar day the client just PICKED in the date strip, not a stored
// instant. parseLocalDate builds a local-midnight Date from the picked Y/M/D
// and toLocaleDateString reads the same local fields back, so the label shows
// the picked day on every device. Converting it to Dubai would shift the
// label a day for users west of UTC.
function readableWhen(ymd, time) {
    const picked = parseLocalDate(ymd);
    if (!picked) return '';
    const label = picked.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
    return time ? `${label} at ${time}` : label;
}

// Client "Request a Booking" PAGE (B1, owner 2026-07-19). The former RequestBookingModal re-homed at
// /client/bookings/request — same form, same quote, same one derived pay
// plan; the shell is a page so the paypage/top-up round-trips and deep links
// land somewhere real and the browser Back button behaves.
//
// Shows the live price (costs only) plus the single Pay-now path (wallet
// toggle decides the rail). When the wallet can't cover it, the deposit form
// opens in place and the booking submits linked to that deposit.
//
// URL contract:
//   ?property=<id>     — pre-select a property
//   ?resumeDraft=1     — prefill from the persisted draft (bookingRequestDraft
//                        — localStorage, so it survives a killed tab or crash,
//                        not just the paypage round-trip) saved right before a
//                        card checkout / top-up redirect. Consumed once at
//                        mount, then stripped from the URL.
export default function RequestBookingPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();

    // Captured ONCE at mount — the draft prefills the initial form state and a
    // later param change must never re-wipe what the client typed since.
    const initialDraftRef = useRef(
        searchParams.get('resumeDraft') === '1' ? loadBookingDraft() : null
    );
    const initialDraft = initialDraftRef.current;
    const initialPropertyId = useRef(
        Number(searchParams.get('property')) || null
    ).current;

    // The resume param has done its job — strip it so refresh/bookmark/share
    // is the plain page (the draft itself stays in storage until submitted).
    useEffect(() => {
        if (searchParams.get('resumeDraft')) {
            const next = new URLSearchParams(searchParams);
            next.delete('resumeDraft');
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Leaving without submitting: back to wherever the client came from, or to
    // My Bookings on a deep link with no in-app history.
    const exitToBookings = () => {
        if (location.key && location.key !== 'default') navigate(-1);
        else navigate('/client/bookings');
    };

    const [properties, setProperties] = useState([]);
    const [propertiesLoaded, setPropertiesLoaded] = useState(false);
    // The selected property's ACTUAL linen set (same property_sq rows the
    // warehouse packs from) — shown inside the included-set disclosure.
    const [includedQuantities, setIncludedQuantities] = useState(null);
    // Pending create_property requests are bookable too (2026-08-04) —
    // they appear in the picker marked pending; staff approve the property
    // first, then the booking.
    const [pendingPropertyRequests, setPendingPropertyRequests] = useState([]);
    const [form, setForm] = useState(() => ({
        property_id: initialDraft?.form?.property_id ?? initialPropertyId,
        pending_property_request_id: initialDraft?.form?.pending_property_request_id ?? null,
        type_id: initialDraft?.form?.type_id ?? 1,
        booking_date: initialDraft?.form?.booking_date ?? '', // 'YYYY-MM-DD'
        booking_time: initialDraft?.form?.booking_time ?? '', // 'HH:mm'
        required_cleaners: initialDraft?.form?.required_cleaners ?? 1,
        duration_hours: initialDraft?.form?.duration_hours ?? 2,
        notes: initialDraft?.form?.notes ?? '',
        selected_addons: initialDraft?.form?.selected_addons ?? [], // [{ item_id, quantity }]
    }));
    // Catalogue of client-selectable add-ons (is_client_addon items).
    const [clientAddons, setClientAddons] = useState([]);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get('/api/client-requests/requests/bookings/client-addons');
                if (!cancelled) setClientAddons(Array.isArray(res.data?.addons) ? res.data.addons : []);
            } catch (e) { if (!cancelled) setClientAddons([]); }
        })();
        return () => { cancelled = true; };
    }, []);
    const [quote, setQuote] = useState(null);
    const [quoteLoading, setQuoteLoading] = useState(false);
    const [quoteError, setQuoteError] = useState('');
    const [showDeposit, setShowDeposit] = useState(false);
    // Server said the wallet path also needs the pending invoices settled
    // (PENDING_INVOICES_DUE) — the deposit form opens prefilled with the
    // combined shortfall instead of a raw-error dead-end.
    const [depositPrefill, setDepositPrefill] = useState(null);
    const [tasks, setTasks] = useState(() => (
        Array.isArray(initialDraft?.tasks) ? initialDraft.tasks : []
    )); // [{ id:null, instruction, is_report_required }]

    // Opening the top-up form snapshots the half-filled request FIRST. The
    // client can now pay for that top-up by card, and a card top-up hands the
    // browser to the hosted paypage — from this component's point of view that
    // is a navigation away with no unmount hook to save anything. Saving here
    // (rather than at the moment of redirect, which happens inside
    // ClientDepositForm) means the draft exists no matter which rail or exit
    // they take. Re-saving is harmless: the draft is a single overwritten key.
    const openDeposit = () => {
        saveBookingDraft({ form, tasks });
        // Mark that THIS top-up came from the booking form, so the paypage
        // return may offer to continue the booking. Without this marker a
        // later invoice/adjustment payment would resurface this draft.
        setTopupBookingIntent('create');
        setShowDeposit(true);
    };

    const [submitting, setSubmitting] = useState(false);
    // Calm, persistent money notice (PAYMENT_IN_REVIEW — the gateway is still
    // holding an earlier approved payment for this intent). Not an error: there
    // is nothing for the client to fix or retry, only to wait for.
    const [payNotice, setPayNotice] = useState('');
    const [error, setError] = useState('');
    // Server blocked the submit because overdue charge adjustments must be
    // settled first — { total, message } (code ADJUSTMENTS_DUE).
    const [adjustmentsDue, setAdjustmentsDue] = useState(null);
    // ZERO FLOOR (P3). The server refused because the spend would take the
    // client's AVAILABLE balance below zero. NOT an error state: nothing was
    // charged and there is nothing to retry, so it renders as the shortfall
    // panel with resolving actions rather than red error text.
    const [walletFloor, setWalletFloor] = useState(null);
    const [done, setDone] = useState(null);
    // Last-minute tick box (the notice itself arrives with the quote — see
    // `notice` below). Cleared whenever the chosen start changes.
    const [lmAck, setLmAck] = useState(false);
    // Payment mode (PLAN_payments Phase 2 + selector UX, 2026-07-15):
    // TWO paths, not three (2026-07-18): 'paynow' — the single primary
    // checkout, where the wallet toggle decides the rail — and 'autocharge', the
    // deliberately separate save-a-card-and-charge-on-completion path. 'paynow'
    // is pre-selected because it is THE way to pay; there is no longer a
    // three-way choice to force the client through.
    const [payMode, setPayMode] = useState('paynow');
    // Card mode: apply the available wallet balance first, card pays the rest.
    const [useWalletBalance, setUseWalletBalance] = useState(true);
    // Set when a card attempt came back WALLET_COVERS_TOTAL and we fell back
    // to the normal wallet submit — shown on the done screen.
    const [walletCoveredNote, setWalletCoveredNote] = useState(false);
    // Saved-card state (PLAN_payments option 3). savedCards = the client's
    // active tokens; cardSelection drives the card-mode paypage (a saved token
    // or a new card, optionally saved). mitEnabled comes from GET
    // /api/payments/mode — customer-absent charging is flag-gated on it.
    const [savedCards, setSavedCards] = useState([]);
    const [cardSelection, setCardSelection] = useState('new'); // card id | 'new'
    // EMBEDDED CARD RAILS (2026-08-16). The card is paid HERE, on the booking
    // page — no paypage, no leaving the form half-filled behind a redirect.
    //   saved card → the code field sits under the picker and its Pay button is
    //     portalled into this page's action row: one step, the same button the
    //     client was already reaching for.
    //   new card / Apple Pay → a SECOND step (payStep = 'card') holding just
    //     the card form. The fields are tall and belong to the gateway; putting
    //     them under a booking form made a page nobody could take in at once.
    // hostedRail 'off' means the SDK could not mount: the primary button falls
    // straight back to the paypage redirect, which is left completely intact.
    const [payStep, setPayStep] = useState('form');
    const [hostedRail, setHostedRail] = useState('probe');
    const [savedCvv, setSavedCvv] = useState('');
    const [savedCardBusy, setSavedCardBusy] = useState(false);
    // The pay button of whichever embedded rail is armed is portalled in here,
    // so this page keeps ONE action row whatever is paying.
    const [paySlot, setPaySlot] = useState(null);
    // Keep this card, on the same terms as every other checkout that offers it.
    const [saveNewCard, setSaveNewCard] = useState(false);
    // What was paid, for the confirmation screen: the figures the client just
    // approved. No receipt handle — this screen links no receipts; they live on
    // the Wallet and Invoices pages.
    const [cardPaid, setCardPaid] = useState(null);
    // The order staged by an attempt that has NOT been paid. A card checkout
    // stages the request server-side as 'awaiting_payment', so walking away from
    // the card form used to leave a request that could be neither paid nor
    // withdrawn — the booking simply said "change requested" forever. Leaving
    // the step releases it.
    const stagedOrderRef = useRef(null);
    const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
    // Read AND kept (item 7): the value used to be discarded (`const [,
    // setMitEnabled]`), so the pre-selection decider could not know whether an
    // auto-charge could actually RUN — with consent ON but MIT off the modal
    // pre-selected a rail with no runnable charge behind it. Fail-closed
    // default: false (unknown MIT state never pre-selects auto-charge).
    const [mitEnabled, setMitEnabled] = useState(false);
    // Which saved card auto-charge uses (defaults to the most recent).
    const [autochargeCardId, setAutochargeCardId] = useState(null);
    // Save-card consent for the auto-charge option when the client has NO card
    // yet (option 3 is now the place a client stores a card — the CARD_CONSENT_TEXT
    // checkbox must be ticked before this path can submit).
    const [autochargeConsent, setAutochargeConsent] = useState(false);
    // AUTO-PAYMENT SIGN-UP (2026-07-18). Auto-charge is a rail this page
    // may only offer as a SELECTION when the client has explicitly signed up in
    // Account → Payment methods. Off ⇒ the option is not selectable at all and
    // the client sees a quiet invitation instead. Fail-closed default: false.
    const [autoPayEnabled, setAutoPayEnabled] = useState(false);
    // The invitation, expanded to its sign-up copy + confirm button.
    const [autoPayInviteOpen, setAutoPayInviteOpen] = useState(false);
    const [autoPaySigningUp, setAutoPaySigningUp] = useState(false);
    // Has the client touched the payment selector? Auto-selecting the signed-up
    // rail must never yank a choice out from under someone mid-flow.
    const payModeTouched = useRef(false);

    const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

    // Client add-on selection: quantity per item_id (0 = not selected).
    const addonQty = (itemId) =>
        form.selected_addons.find((a) => Number(a.item_id) === Number(itemId))?.quantity || 0;
    const setAddonQty = (itemId, qty) => {
        setForm((prev) => {
            const others = prev.selected_addons.filter((a) => Number(a.item_id) !== Number(itemId));
            const q = Math.max(0, Math.min(20, Math.round(qty)));
            return { ...prev, selected_addons: q > 0 ? [...others, { item_id: Number(itemId), quantity: q }] : others };
        });
    };
    const isHourly = HOURLY_TYPE_IDS.includes(form.type_id);

    // Restored from the paypage's Back button (bfcache) → the "Submitting…"
    // freeze from right before the redirect must clear so the page is usable.
    useBfcacheReset(() => setSubmitting(false));

    // A time slot needs at least 1.5 HOURS of lead time — judged on the DUBAI
    // clock (the backend enforces the same rule / BOOKING_TOO_SOON in
    // Asia/Dubai), so a client browsing from another timezone sees the same
    // availability and never hits the server rejection blind.
    const MIN_LEAD_MINUTES = 90;
    const isSlotTooSoon = (t) => {
        if (!form.booking_date) return false;
        const when = DateTime.fromISO(`${form.booking_date}T${t}`, { zone: 'Asia/Dubai' });
        return when.isValid
            ? when < DateTime.now().setZone('Asia/Dubai').plus({ minutes: MIN_LEAD_MINUTES })
            : false;
    };

    // Clear a chosen time that becomes past after changing the date.
    useEffect(() => {
        if (form.booking_time && isSlotTooSoon(form.booking_time)) {
            set({ booking_time: '' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.booking_date]);

    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get('/api/client-requests/properties');
                setProperties(Array.isArray(res.data?.properties) ? res.data.properties : []);
            } catch (err) {
                console.error('Error fetching properties:', err);
            }
            try {
                const pr = await axios.get('/api/client-requests/requests/my', {
                    params: { status: 'pending', type: 'create_property' },
                });
                setPendingPropertyRequests(Array.isArray(pr.data?.requests) ? pr.data.requests : []);
            } catch (e) {
                setPendingPropertyRequests([]);
            } finally {
                setPropertiesLoaded(true);
            }
        })();
    }, []);

    useEffect(() => {
        if (!form.property_id) { setIncludedQuantities(null); return; }
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`/api/client-requests/properties/${form.property_id}/included-quantities`);
                if (!cancelled) setIncludedQuantities(res.data);
            } catch (err) {
                if (!cancelled) setIncludedQuantities(null); // degrade to the generic text
            }
        })();
        return () => { cancelled = true; };
    }, [form.property_id]);

    // Pending-invoices notice (2026-07-17: every payment place reminds the
    // client of pending invoices). DISPLAY ONLY — no gating; the figure is the
    // outstanding-invoices total (pending-breakdown, invoices-only) and degrades
    // silently to hidden if the fetch fails.
    const [pendingInvoicesTotal, setPendingInvoicesTotal] = useState(0);
    // The submit button must NOT be pressable while this fetch is in flight
    // (2026-08-09) — the client has to SEE their pending
    // amount before committing. Resolved on success AND on failure (failure
    // degrades to no-notice exactly as before, but only once we know).
    const [pendingCheckDone, setPendingCheckDone] = useState(false);
    // Per-entity split of the pending total (multi-billing clients only — the
    // server sends entity_breakdown: null for everyone else, so the breakdown
    // renders exactly as before). Display-only sub-rows.
    const [pendingEntityBreakdown, setPendingEntityBreakdown] = useState(null);
    useEffect(() => {
        let cancelled = false;
        // Separate entity wallets: the rider settles the pool of the wallet
        // THIS booking charges (the quote says which); until the quote is in,
        // the main pool as before.
        const riderWallet = quote?.wallet_client_id || null;
        axios.get('/api/client-requests/invoices/pending-breakdown', { params: riderWallet ? { wallet: riderWallet } : {} })
            .then((res) => {
                if (cancelled) return;
                setPendingInvoicesTotal(Math.max(0, Number(res.data?.total || 0)));
                setPendingEntityBreakdown(
                    Array.isArray(res.data?.entity_breakdown) && res.data.entity_breakdown.length > 0
                        ? res.data.entity_breakdown
                        : null
                );
            })
            .catch(() => { /* notice simply doesn't show */ })
            .finally(() => { if (!cancelled) setPendingCheckDone(true); });
        return () => { cancelled = true; };
    }, [quote?.wallet_client_id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Saved cards + MIT flag (both best-effort — the page must work even if the
    // payments/cards endpoints aren't deployed). Default the card-mode picker
    // and the auto-charge card to the most recent saved card.
    // Refetchable so the auto-pay sign-up rail can re-read the account when the
    // server says a card already exists (CARD_ALREADY_SAVED — one landed in
    // another tab between load and submit).
    const loadCards = useCallback(async () => {
        try {
            const res = await axios.get('/api/cards');
            const list = Array.isArray(res.data?.cards) ? res.data.cards : [];
            setSavedCards(list);
            const enrolled = readAutoPayEnabled(res.data);
            setAutoPayEnabled(enrolled);
            if (list.length > 0) {
                setCardSelection(list[0].id);
                setAutochargeCardId(list[0].id);
            }
            // NOTE (item 7, 2026-07-19): this used to pre-select
            // 'autocharge' UNCONDITIONALLY for an enrolled client with a card.
            // Wrong for the refined hierarchy — ANY available balance (even
            // 5 AED) means wallet-first Pay now. Pre-selection is now the
            // reevaluatePayMode effect below, which runs when the QUOTE
            // arrives (the balance is unknown here at cards-load time).
        } catch (err) {
            if (err.response?.status !== 404) console.error('Error loading saved cards:', err);
            setAutoPayEnabled(false); // fail closed
        }
    }, []);

    useEffect(() => {
        (async () => {
            await loadCards();
            try {
                const res = await axios.get('/api/payments/mode');
                setMitEnabled(!!res.data?.mit_enabled);
            } catch { /* leave auto-charge hidden */ }
        })();
    }, [loadCards]);

    const propertyOptions = useMemo(
        () => [
            ...properties.map((p) => ({
                value: p.property_id,
                label: `Unit ${p.unit_number} · ${p.building_name}`,
            })),
            ...pendingPropertyRequests.map((r) => ({
                value: `pr-${r.id}`,
                label: `Unit ${r.payload?.unit_number || '—'} · ${r.payload?.address_line_1 || 'Pending property'} (pending approval)`,
            })),
        ],
        [properties, pendingPropertyRequests]
    );

    const wallTimeStart = form.booking_date && form.booking_time
        ? `${form.booking_date} ${form.booking_time}:00`
        : '';

    // Live estimate + wallet panel (debounced). While recalculating, the panel
    // shows a loading state and Submit is blocked.
    useEffect(() => {
        if ((!form.property_id && !form.pending_property_request_id) || !wallTimeStart) {
            setQuote(null);
            setQuoteLoading(false);
            setQuoteError('');
            return undefined;
        }
        setQuoteLoading(true);
        const handle = setTimeout(async () => {
            try {
                const res = await axios.post('/api/client-requests/requests/bookings/estimate', {
                    property_id: form.property_id,
                    pending_property_request_id: form.pending_property_request_id,
                    type_id: form.type_id,
                    booking_start: wallTimeStart,
                    required_cleaners: form.required_cleaners,
                    duration_hours: form.duration_hours,
                    selected_addons: form.selected_addons,
                });
                setQuote(res.data);
                setQuoteError('');
            } catch (err) {
                setQuote(null);
                setQuoteError(err.response?.data?.error || 'Could not calculate the price.');
            } finally {
                setQuoteLoading(false);
            }
        }, 350);
        return () => clearTimeout(handle);
    }, [form.property_id, form.pending_property_request_id, form.type_id, wallTimeStart, form.required_cleaners, form.duration_hours, form.selected_addons]);

    // LAST-MINUTE / OUTSIDE-OFFICE-HOURS NOTICE. Decided by the server on the
    // Dubai clock and returned with the quote (routes/services/bookingNotice.js)
    // — wording included — so this page and the mobile app always say the same
    // thing. A last-minute start needs the tick before ANY way of paying: every
    // submit button on this page is gated on canSubmit, and every submit sends
    // buildBookingPayload(), so the one flag covers them all.
    const notice = quote?.notice || null;
    const ackRequired = !!notice?.ack_required;
    useEffect(() => { setLmAck(false); }, [wallTimeStart]);

    const canSubmit = (form.property_id || form.pending_property_request_id) && wallTimeStart && quote && !quoteLoading
        && (!ackRequired || lmAck);

    // AVAILABLE balance (net of pending reservations) — the same number the
    // server's sufficiency gate uses. All Use-Wallet summary copy is based on
    // it, never the gross wallet total (2026-07-15).
    const availableRaw = quote ? Number(quote.available || 0) : 0;
    // ONE derived plan (2026-07-18). The client no longer chooses a rail
    // between "Use wallet" and "Pay now by card" — there is one Pay-now path and
    // one toggle, and resolvePayNow decides the rail arithmetically. DISPLAY
    // ONLY: the server recomputes authoritatively from the gateway-side booking
    // payload. `availableRaw` may legitimately be negative (P3) — resolvePayNow
    // floors it internally for the contribution maths without hiding it.
    const payPlan = resolvePayNow({
        amount: quote ? Number(quote.estimate.total) : 0,
        spendable: availableRaw,
        useWallet: useWalletBalance,
    });
    const applyWallet = payPlan.walletPortion > 0.005;
    const cardPortion = payPlan.cardPortion;
    // A4 (2026-07-19): the checkout sum reads additions FIRST
    // (+ booking, + pending invoices), then ONE wallet deduction against the
    // combined total, then one "left to pay". Derived from the ONE plan by the
    // shared helper — no second arithmetic path. When the wallet covers the
    // whole combined total the server settles P from the wallet itself
    // (settlePendingFromWallet) and the deduction is walletPortion + P;
    // anything less and P rides the card capture (combined.leftToPay — exactly
    // what the gateway collects) while the wallet deduction stays CLAMPED to
    // what the wallet actually holds. combined.needsCard is THE card-leg
    // predicate here — payPlan.needsCard only knows about the booking and is
    // false in the band where the wallet covers the booking but not booking+P,
    // where the card still collects exactly P.
    const combined = combinedCheckoutFigures(payPlan, pendingInvoicesTotal);
    // MANDATORY pending-invoice settlement (owner 2026-07-17 — not optional):
    // whenever this checkout has a card leg, that ONE capture settles P too
    // (the server forces it; these figures are display only, the server
    // recomputes the fresh total authoritatively). Even when the wallet covers
    // the whole booking (cardPortion 0) the card still collects exactly P.
    // Only a wallet that covers booking + P settles P from the wallet instead.
    const cardSettlesPending = payMode === 'paynow' && combined.needsCard && pendingInvoicesTotal > 0;

    // Does this submission have a CARD leg at all? One boolean, used by the
    // primary button, the submit routing and the card picker, so they can
    // never disagree about whether a card is involved.
    const cardLegExists = !!quote
        && (cardPortion > 0.005 || cardSettlesPending)
        && !quote.covered_by_pending_deposits;
    // The auto-charge rail has its own fixed label (autoPaySetupCopy) and never
    // captures a booking amount here, so the pay-now primary is only this.
    const showPayNowPrimary = payMode !== 'autocharge' && cardLegExists;

    // WHICH EMBEDDED RAIL, IF ANY, IS ARMED.
    // A stored card is paid on this page: the code sits under the picker and
    // the Pay button takes the primary's place in the action row. A new card
    // (or Apple Pay) goes to step 2, which owns the action row while it is up.
    // Both stand down when the SDK cannot mount — the paypage redirect is
    // untouched underneath.
    const embeddedCardRail = hostedRail !== 'off';
    const selectedSavedCard = savedCards.find((c) => String(c.id) === String(cardSelection)) || null;
    const savedCardActive = payMode === 'paynow' && cardLegExists && !!selectedSavedCard;
    const embeddedPayOwnsButton = payStep === 'card' || savedCardActive;

    // "Save card & pay automatically" (option 3) is where a client stores a
    // card, so it must appear even when they don't have one yet (they save one
    // here); if they already have cards we let them pick which one auto-charges.
    // NOTE: the actual unattended charge at booking completion only fires once
    // N-Genius enables MoTo/MIT (mitEnabled / NGENIUS_MIT_ENABLED).
    //
    // GATED ON THE SIGN-UP (2026-07-18). Selectable ONLY for clients who
    // signed up in Account → Payment methods. Everyone else sees the invitation
    // below and pays the normal way; "toggled off ⇒ never auto charged" is
    // enforced here, in the reset effect, and again at submit.
    // AUTO_CHARGE_UI_ENABLED is the master hide-switch (2026-07-20): while
    // MoTo/MIT is not live we offer no auto-charge anywhere, so this is false and
    // the option, the sign-up invite, and any pre-selection all collapse. An
    // already-enrolled client (server consent = true) is transparently kept on
    // Pay now: enrolled below reads false, reevaluatePayMode never returns
    // 'autocharge', and the reset effect flips any stale selection back.
    const autochargeAvailable = AUTO_CHARGE_UI_ENABLED && autoPayEnabled;
    const hasSavedCard = savedCards.length > 0;
    // CHARGEABLE (item 7, the decider's branch-2 gate): could a pre-selected
    // auto-charge actually run? = MIT enabled AND (a card on file for the
    // unattended rail OR the first-card AED 1.00 setup rail). THIS surface
    // always offers the setup rail when no card exists (submitAutopaySetup),
    // so the card-or-setup-rail term is satisfied either way and MIT is the
    // one live gate — exactly mobile's decider (autoPayConsent.defaultPayMode),
    // which likewise never requires a card for rule 2. Strict `=== true`: an
    // unknown /api/payments/mode answer fails closed to not-chargeable, so
    // consent ON with no runnable rail lands on Pay now + the quiet invite
    // instead of pre-selecting an auto-charge that cannot happen.
    const autochargeChargeable = mitEnabled === true;
    const autochargeCard = savedCards.find((c) => c.id === autochargeCardId) || savedCards[0] || null;
    // No-card auto-charge = the save-a-card path; the consent box must be ticked
    // before it can submit (mirrors the card-save consent gate everywhere else).
    const autochargeConsentBlocked = payMode === 'autocharge' && !hasSavedCard && !autochargeConsent;

    // If the auto-charge option disappears while it's selected (flag flipped /
    // card removed mid-flow), fall back to no selection so submit can't misfire.
    // Covers the TOUCHED case too — reevaluatePayMode below deliberately never
    // overrides a manual choice, but a rail that no longer exists must not stay
    // armed whoever selected it.
    useEffect(() => {
        if (payMode === 'autocharge' && !autochargeAvailable) setPayMode('paynow');
    }, [payMode, autochargeAvailable]);

    // PRE-SELECTION — the owner's checkout hierarchy (item 7, 2026-07-19), ONE
    // shared definition: reevaluatePayMode/defaultPayMode in autoPay.js
    // (mirroring mobile's autoPayConsent.ts so the platforms cannot drift).
    //   1. ANY available balance (even 5 AED) → Pay now, wallet toggle on. A
    //      partial balance splits with the card in the SAME Pay now action —
    //      it never falls through to auto-charge.
    //   2. balance ZERO and signed up AND chargeable (MIT on, with a saved
    //      card or the first-card AED 1.00 verification rail) → Auto Payment.
    //   3. otherwise → Pay now (card) + the quiet invite. Consent ON but NOT
    //      chargeable (MIT off) lands here too — never a pre-selected
    //      auto-charge that cannot run.
    // The balance figure is the plain AVAILABLE balance off the quote (item 12:
    // due adjustments never deduct from it), which is UNKNOWN at mount — so
    // this re-runs when the QUOTE arrives (not just when cards load) and
    // re-applies the hierarchy, failing closed to Pay now while it is unknown.
    // A manual choice (payModeTouched) is NEVER overridden, including by the
    // late quote.
    useEffect(() => {
        if (submitting) return;
        const next = reevaluatePayMode({
            current: payMode,
            touched: payModeTouched.current,
            enrolled: autochargeAvailable,
            // Branch 2's second gate — MIT on + (card on file or the setup
            // rail). False until /api/payments/mode answers, so an unknown
            // capability fails closed to 'paynow'.
            chargeable: autochargeChargeable,
            // undefined until the quote arrives — the hierarchy needs the real
            // figure and fails closed to 'paynow' without it.
            available: quote ? Number(quote.available) : undefined,
            cost: quote ? Number(quote.estimate.total) : undefined,
        });
        if (next !== payMode) setPayMode(next);
    }, [submitting, payMode, autochargeAvailable, autochargeChargeable, quote]);

    // Sign up from checkout. This is an EXPLICIT act behind its own button —
    // the invitation itself never selects the auto-charge rail. Only after the
    // server confirms enrolment do we switch the selection over.
    const signUpForAutoPay = async () => {
        if (autoPaySigningUp) return;
        setAutoPaySigningUp(true);
        setError('');
        try {
            const saved = await saveAutoPayEnabled(true);
            setAutoPayEnabled(saved);
            if (saved) {
                payModeTouched.current = true;
                setPayMode('autocharge');
                setAutoPayInviteOpen(false);
            }
        } catch (err) {
            console.error('Error signing up for automatic payments:', err);
            setError(paymentErrorMessage(err, 'Could not switch on automatic payments. Please try again.'));
        } finally {
            setAutoPaySigningUp(false);
        }
    };

    // The one booking payload — the wallet submit and the card checkout send
    // the SAME body (the checkout endpoint validates it identically and adds
    // use_wallet on top). Keep them from drifting.
    const buildBookingPayload = (linkedDepositId = null) => ({
        property_id: form.property_id,
        pending_property_request_id: form.pending_property_request_id,
        type_id: form.type_id,
        booking_start: wallTimeStart,
        required_cleaners: form.required_cleaners,
        duration_hours: form.duration_hours,
        notes: form.notes,
        selected_addons: form.selected_addons,
        tasks: tasks
            .map((t) => ({ instruction: (t.instruction || '').trim(), is_report_required: !!t.is_report_required }))
            .filter((t) => t.instruction),
        linked_deposit_id: linkedDepositId,
        // Booking notice: this build shows the last-minute tick box, and
        // whether the client ticked it (the server enforces and records it).
        notice_version: 1,
        last_minute_ack: lmAck === true,
    });

    // The server found the start is last-minute but the tick wasn't sent — the
    // form was open across 8 pm or midnight, so the quote on screen said
    // otherwise. Adopt the server's notice so the tick box appears.
    const adoptServerNotice = (err) => {
        const data = err?.response?.data;
        if (data?.code !== 'LAST_MINUTE_ACK_REQUIRED') return false;
        if (data.notice) setQuote((q) => (q ? { ...q, notice: data.notice } : q));
        setLmAck(false);
        return true;
    };

    // Overdue charge adjustments block NEW booking requests (both the wallet
    // submit and the card checkout — the server gates each with code
    // ADJUSTMENTS_DUE). Returns true when handled so callers can stop.
    const handleAdjustmentsDue = (err) => {
        const data = err.response?.data;
        if (data?.code !== 'ADJUSTMENTS_DUE') return false;
        const total = Number(data.total_due ?? data.due_total ?? data.total ?? 0);
        setAdjustmentsDue({
            total,
            message: data.error
                || 'You have overdue price adjustments that must be settled before submitting new booking requests.',
        });
        setError('');
        return true;
    };

    // Other known 400 codes → guided recovery instead of raw error dead-ends.
    // Returns true when handled so callers can stop.
    const handleKnownCodes = (err) => {
        const data = err.response?.data || {};
        if (data.code === 'PENDING_INVOICES_DUE') {
            // DRIFT/RACE fallback only: the wallet submit is routed via
            // cardLegExists, which sends any wallet-short-of-booking+P case to
            // the card checkout (where P rides the capture). This 400 can still
            // arrive when P appeared or the balance shrank between quoting and
            // submitting — adopt the fresh figure and open the top-up form
            // prefilled with the missing amount instead of a bare error.
            const freshP = Number(data.pending_total ?? data.total ?? 0);
            if (freshP > 0) setPendingInvoicesTotal(freshP);
            const short = Number(data.shortfall ?? 0);
            const base = short > 0 ? short : (freshP > 0 ? freshP : pendingInvoicesTotal);
            setDepositPrefill(Math.max(100, Math.ceil(base / 100) * 100));
            openDeposit();
            setError(data.error
                || 'Your pending invoices are settled together with this booking — top up the difference to continue.');
            return true;
        }
        if (data.code === 'BOOKING_TOO_SOON') {
            setError('That start time is less than 1.5 hours away (Dubai time) — please pick a later start time.');
            set({ booking_time: '' });
            return true;
        }
        if (adoptServerNotice(err)) {
            // Stay on the step the client is on (a typed card is not thrown
            // away) — the tick box sits above the action row on every step.
            setError(data.message || data.error || '');
            return true;
        }
        if (data.code === 'PENDING_DRIFT') {
            // The pending-invoices total changed between quoting and submitting
            // — adopt the fresh figure inline and let the client re-confirm.
            const fresh = Number(data.breakdown?.total ?? data.pending_total ?? data.total ?? NaN);
            if (Number.isFinite(fresh)) setPendingInvoicesTotal(Math.max(0, fresh));
            setError(`Your outstanding total changed to ${money(Number.isFinite(fresh) ? Math.max(0, fresh) : pendingInvoicesTotal)} — review and confirm.`);
            return true;
        }
        // ZERO FLOOR (P3). INSUFFICIENT_BALANCE / WALLET_AVAILABLE_FLOOR /
        // WOULD_GO_NEGATIVE all mean the same thing to the client: available
        // does not cover this and NOTHING WAS CHARGED. Previously this
        // force-opened the top-up form and set red error text — one action, no
        // shortfall figure, and a card payment (usually the cheaper answer)
        // wasn't offered at all. Now it hands the server's own figures to the
        // shortfall panel, which offers card / top-up / reduce in that order.
        const floor = readWalletFloor(err);
        if (floor) {
            setWalletFloor(floor);
            if (floor.shortfall != null && floor.shortfall > 0) {
                setDepositPrefill(Math.max(100, Math.ceil(floor.shortfall / 100) * 100));
            }
            // Deliberately NOT setError(): the panel is the message, and a red
            // string beside it would restate a block as a failure.
            setError('');
            return true;
        }
        return false;
    };

    const submitRequest = async (linkedDepositId = null) => {
        setSubmitting(true);
        setError('');
        setAdjustmentsDue(null);
        try {
            await axios.post('/api/client-requests/requests/bookings', buildBookingPayload(linkedDepositId));
            clearBookingDraft(); // submitted — the belt-and-braces draft is done
            setDone(linkedDepositId ? 'with_deposit' : 'direct');
        } catch (err) {
            if (!handleAdjustmentsDue(err) && !handleKnownCodes(err)) {
                console.error('Error submitting booking request:', err);
                setError(paymentErrorMessage(err, 'Failed to submit booking request.'));
            }
        } finally {
            setSubmitting(false);
        }
    };

    // Pay now by card: create the request + hosted checkout in one call, then
    // hand the browser to the paypage. The payment-return page handles the
    // return and only announces the request once the gateway confirms the
    // payment. The wallet/card split shown in the UI is display-only — the
    // server recomputes it authoritatively from the same booking payload.
    const submitCardCheckout = async () => {
        setSubmitting(true);
        setError('');
        setAdjustmentsDue(null);
        // Belt-and-braces: snapshot the filled form BEFORE handing the browser
        // to the paypage, so cancelling the payment can offer "Edit & resubmit"
        // with everything prefilled.
        saveBookingDraft({ form, tasks });
        try {
            const res = await axios.post('/api/payments/request-checkout', {
                ...buildBookingPayload(null),
                use_wallet: applyWallet,
                // NOTE: the server ALWAYS settles the pending invoices inside this
                // capture when P > 0 (mandatory — no client flag is sent or trusted).
                // payment_method_id (pay the card portion with a saved token) for
                // a chosen saved card. Card-SAVING now lives in the dedicated
                // "Save card & pay automatically" option, so this box never saves
                // (saveNew is always false here → a plain one-off new card).
                ...cardCheckoutBody(cardSelection, false),
            });
            if (res.data?.payment_url) {
                window.location.assign(res.data.payment_url);
                return; // keep `submitting` on — we are navigating away
            }
            // A checkout without a paypage URL would strand a staged request —
            // surface it instead of navigating to "<origin>/null".
            setError('Could not open the secure payment page. Please try again.');
            setSubmitting(false);
        } catch (err) {
            if (['WALLET_COVERS_TOTAL', 'NOTHING_TO_PAY'].includes(err.response?.data?.code)) {
                // The wallet already covers the whole total — no card payment
                // to make. Fall back to the normal wallet submit.
                setWalletCoveredNote(true);
                await submitRequest();
                return;
            }
            if (isPaymentInReview(err)) {
                // An earlier card payment for this booking is still being held by
                // the gateway — starting a second one could double-charge.
                setPayNotice(paymentInReviewMessage(err));
                setError('');
            } else if (!handleAdjustmentsDue(err) && !handleKnownCodes(err)) {
                console.error('Error starting card checkout:', err);
                setError(paymentErrorMessage(err, 'Could not start the card payment.'));
            }
            setSubmitting(false);
        }
    };

    // ── EMBEDDED CARD RAILS ────────────────────────────────────────────────
    // Both post the SAME body to the SAME endpoint as the redirect rail above:
    // the booking payload, the wallet flag, and one extra field (the SDK's
    // session, or the stored card's code). The server prices, stages and
    // captures exactly as before — only the gateway leg differs — so nothing
    // about what gets booked or charged depends on which rail the client used.

    // Shared failure handling for both: the same cases submitCardCheckout has
    // always handled, in one place rather than copied per rail.
    const handleCheckoutError = async (err) => {
        if (['WALLET_COVERS_TOTAL', 'NOTHING_TO_PAY'].includes(err.response?.data?.code)) {
            // The wallet grew (or the invoices were settled elsewhere) while the
            // form was open: there is no card leg left. Book it from the wallet.
            setWalletCoveredNote(true);
            setPayStep('form');
            await submitRequest();
            return true;
        }
        if (isPaymentInReview(err)) {
            setPayNotice(paymentInReviewMessage(err));
            setError('');
            return true;
        }
        if (handleAdjustmentsDue(err) || handleKnownCodes(err)) return true;
        return false;
    };

    // NEW CARD: the SDK's one-time session completes the payment server-side;
    // the panel then runs any 3DS in its own overlay and verifies through
    // /payments/verify. Returning null tells the panel to stop — never hand it
    // an undefined payment to authenticate.
    const startHostedPayment = async (sessionId) => {
        setError('');
        saveBookingDraft({ form, tasks }); // a 3DS overlay can still end in a reload
        try {
            const res = await axios.post('/api/payments/request-checkout', {
                ...buildBookingPayload(null),
                use_wallet: applyWallet,
                session_id: sessionId,
                ...(saveNewCard ? { save_card: true, save_card_consent: true } : {}),
            });
            if (!res.data?.payment_response) {
                setError('Could not start the card payment. Please try again.');
                return null;
            }
            stagedOrderRef.current = res.data.order_ref || null;
            return res.data;
        } catch (err) {
            if (!(await handleCheckoutError(err))) {
                console.error('Error starting embedded card payment:', err);
                setError(paymentErrorMessage(err, 'Could not start the card payment.'));
            }
            return null;
        }
    };

    // SAVED CARD: same call plus the card and its code. The response IS the
    // outcome (paid / failed / a 3DS challenge to drive through our relay), so
    // SavedCardPay reads it the same way it does on every other surface.

    // Leaving the page with an unpaid attempt behind releases it too — a closed
    // tab is the same intent as Back, and sendBeacon survives the unload.
    useEffect(() => {
        const release = () => {
            const ref = stagedOrderRef.current;
            if (!ref) return;
            stagedOrderRef.current = null;
            try {
                const blob = new Blob([JSON.stringify({ order_ref: ref })], { type: 'application/json' });
                navigator.sendBeacon?.('/api/payments/cancel', blob);
            } catch { /* the sweep expires it */ }
        };
        window.addEventListener('pagehide', release);
        return () => {
            window.removeEventListener('pagehide', release);
            release();
        };
    }, []);

    // RELEASE AN UNPAID ATTEMPT. Cancelling the checkout tears down the request
    // it staged (and any pending deposit behind it) through the same path the
    // client cancel endpoint uses, so nothing is left waiting on a payment that
    // is never coming. Refused by the server while money is actually in flight —
    // that call is its own — and best-effort otherwise: the checkout sweep is
    // still the backstop.
    const releaseStagedCheckout = async () => {
        const ref = stagedOrderRef.current;
        if (!ref) return;
        stagedOrderRef.current = null;
        try {
            await axios.post('/api/payments/cancel', { order_ref: ref });
        } catch { /* the sweep expires it */ }
    };

    const chargeSavedCardNow = async (cvv) => {
        setError('');
        saveBookingDraft({ form, tasks });
        try {
            const res = await axios.post('/api/payments/request-checkout', {
                ...buildBookingPayload(null),
                use_wallet: applyWallet,
                ...cardCheckoutBody(cardSelection, false),
                cvv,
            });
            return res.data;
        } catch (err) {
            // Nothing was charged. Bring up the tick box; the card row states
            // the outcome itself, as it does for every other refusal.
            adoptServerNotice(err);
            throw err;
        }
    };

    // Verified paid, by either rail: the request is already committed
    // server-side by confirmCheckout, so this is purely the confirmation of
    // what was charged.
    const onCardPaid = () => {
        stagedOrderRef.current = null;
        clearBookingDraft();
        setPayStep('form');
        setCardPaid({
            total: combined.leftToPay,
            booking: Math.max(0, round2(combined.leftToPay - (cardSettlesPending ? pendingInvoicesTotal : 0))),
            invoices: cardSettlesPending ? pendingInvoicesTotal : 0,
        });
        setDone('card');
    };


    // Auto-charge (option 3): submit a normal booking request flagged to charge
    // the saved card automatically WHEN the cleaning is completed. No paypage,
    // no upfront charge — the card is charged later by the MIT engine. Handles
    // MIT_DISABLED defensively (the flag can flip off between load and submit).
    const submitAutocharge = async () => {
        setSubmitting(true);
        setError('');
        setAdjustmentsDue(null);
        try {
            await axios.post('/api/payments/request-autocharge', {
                ...buildBookingPayload(null),
                payment_method_id: autochargeCardId,
            });
            clearBookingDraft();
            setDone('autocharge');
        } catch (err) {
            const code = err.response?.data?.code;
            if (code === 'MIT_DISABLED') {
                // Auto-charge was turned off — hide the option and make them pick again.
                setMitEnabled(false);
                setPayMode('paynow');
                setError('Auto-charge isn’t available right now. Please choose another way to pay.');
            } else if (code === 'NO_SAVED_CARD') {
                setSavedCards([]);
                setPayMode('paynow');
                setError('That saved card is no longer available. Please choose another way to pay.');
            } else if (!handleAdjustmentsDue(err)) {
                console.error('Error submitting auto-charge booking:', err);
                setError(paymentErrorMessage(err, 'Could not submit the booking.'));
            }
            setSubmitting(false);
        }
    };

    // AUTO-PAY FIRST-CARD SIGN-UP (owner 2026-07-18, money-critical).
    //
    // The client chose "pay when the cleaning is completed" but has no token on
    // file yet. Auto-charge cannot be staged without a card, so we tokenise
    // FIRST — through the SAME temporary AED 1.00 verification hold that
    // /api/cards/setup places (released automatically; nothing is captured and
    // no wallet row is created) — and the booking is staged in auto_charge mode
    // behind it. The ONE thing this path must never do is capture the booking
    // total: choosing auto-pay means no booking money moves until completion.
    // The amount is fixed server-side at the literal 1 and is never derived
    // from the estimate; nothing here sends or implies a booking figure.
    //
    // PENDING INVOICES are deliberately NOT rolled into this rail (see the
    // note beside the copy below). An AED 1.00 verification hold is not a
    // booking payment, and the saved-card auto-charge path already leaves P
    // untouched — settling it here would make the same choice behave
    // differently purely on whether a token happened to exist. They stay due
    // and the UI says so.
    const submitAutopaySetup = async () => {
        setSubmitting(true);
        setError('');
        setAdjustmentsDue(null);
        // The browser is about to leave for the paypage — snapshot the filled
        // form so cancelling can offer "Edit & resubmit" fully prefilled.
        saveBookingDraft({ form, tasks });
        try {
            const res = await axios.post('/api/payments/request-autopay-setup', {
                ...buildBookingPayload(null),
                save_card_consent: true,
                consent_text_version: CARD_CONSENT_VERSION,
            });
            if (res.data?.payment_url) {
                window.location.assign(res.data.payment_url);
                return; // keep `submitting` on — we are navigating away
            }
            setError('Could not open the secure payment page. Please try again.');
            setSubmitting(false);
        } catch (err) {
            const code = err.response?.data?.code;
            if (code === 'MIT_DISABLED') {
                setMitEnabled(false);
                setPayMode('paynow');
                setError('Auto-charge isn’t available right now. Please choose another way to pay.');
            } else if (code === 'CARD_ALREADY_SAVED') {
                // A card landed between load and submit (another tab, a
                // concurrent top-up). The correct rail is now the no-capture
                // one — reload the cards and let them submit again.
                loadCards();
                setError('A saved card is now on your account — press Submit again to book with automatic payment.');
                setSubmitting(false);
            } else if (code === 'AUTOPAY_NOT_ENABLED') {
                setAutoPayEnabled(false);
                setPayMode('paynow');
                setError('Automatic payments are switched off for your account. Please choose how to pay.');
            } else if (isPaymentInReview(err)) {
                setPayNotice(paymentInReviewMessage(err));
                setError('');
            } else if (!handleAdjustmentsDue(err) && !handleKnownCodes(err)) {
                console.error('Error starting auto-pay card setup:', err);
                setError(paymentErrorMessage(err, 'Could not start the card verification.'));
            }
            if (code !== 'CARD_ALREADY_SAVED') setSubmitting(false);
        }
    };

    const handleSubmitClick = () => {
        // A fresh attempt clears the previous floor block — otherwise the panel
        // outlives the shortfall it described (e.g. after a top-up lands).
        setWalletFloor(null);
        if (!canSubmit) {
            setError('Please select a property, service and a date & time.');
            return;
        }
        // No payment mode chosen → nothing to do (the button is disabled with
        // a hint; this guard keeps the flows unreachable regardless).
        if (payMode !== 'paynow' && payMode !== 'autocharge') return;
        if (payMode === 'autocharge') {
            // LAST LINE OF DEFENCE. If the sign-up is not on (state raced, the
            // client turned it off in another tab, the field is missing), the
            // auto-charge rail must not be submitted — fall back to Pay now
            // rather than enrol anyone by accident.
            if (!autoPayEnabled) {
                setPayMode('paynow');
                setError('Automatic payments are switched off for your account. Please choose how to pay.');
                return;
            }
            // NO CARD ON FILE → the sign-up rail. Tokenise with the temporary
            // AED 1.00 verification hold (released automatically), then the
            // staged request rides the SAME auto-charge path as a client who
            // already had a card. Never a booking capture.
            // (Before this, the form posted request-autocharge regardless and
            // the server 400'd NO_SAVED_CARD — a dead end that stored nothing.)
            if (!hasSavedCard) {
                submitAutopaySetup();
                return;
            }
            submitAutocharge();
            return;
        }
        // PAY NOW. The rail is derived, not chosen:
        //   wallet covers booking + P (or a pending top-up covers the booking
        //   and no P rides)             → wallet submit
        //   anything left to pay        → card checkout, with the wallet leg
        //                                 applied and P riding the SAME capture
        // The card leg comes from the COMBINED figures (cardLegExists), never
        // payPlan.needsCard alone: when the wallet covers the booking but not
        // booking + P, the wallet submit is a known 400 (PENDING_INVOICES_DUE)
        // — the card checkout collects exactly the P remainder instead, the
        // same routing as mobile's CreateBookingSheet.
        if (cardLegExists) {
            // A stored card never reaches here — its own Pay button (portalled
            // into this row) replaces the primary. A new card goes to the card
            // step; only a browser the SDK cannot serve still redirects.
            if (embeddedCardRail) {
                setError('');
                setPayStep('card');
                return;
            }
            submitCardCheckout();
            return;
        }
        if (quote.sufficient || quote.covered_by_pending_deposits) {
            // Covered-by-pending rides on the already-pending top-up — the
            // backend accepts it without a linked deposit.
            submitRequest();
        } else {
            openDeposit();
        }
    };

    const handleDepositSuccess = (deposit) => {
        setShowDeposit(false);
        submitRequest(deposit?.id || null);
    };

    // Shortfall is what's still missing AFTER pending top-up headroom (matches
    // the server's INSUFFICIENT_BALANCE gate); the deposit form prefills it
    // rounded UP to the next 100.
    const rawShortfall = quote && !quote.sufficient
        ? Math.max(0, Number(quote.shortfall_after_pending
            ?? (quote.estimate.total - (quote.available ?? 0) - (quote.pending_deposit_headroom ?? 0))))
        : 0;
    const shortfall = Math.ceil(rawShortfall / 100) * 100;

    // The GROSS wallet-math derivations (wallet total, pending-request /
    // pending-top-up gross rows, "balance if all confirmed") lived here to
    // feed a SECOND standalone arithmetic block under the pay breakdown. That
    // block is gone (item 13, owner 2026-07-19 screenshot: two different
    // wallet calculations on one sheet — "makes 0 sense... absolutely
    // confusing"), so the numbers went with it rather than being left computed
    // and unread. The pay breakdown above is the ONE place checkout arithmetic
    // renders: one fact, one rendering. Only the pending-top-up COUNT
    // survives — the covered-by-pending sentence pluralises with it.
    const pendingTopupsCount = quote ? Number(quote.pending_deposits_all_count || 0) : 0;

    return (
        <div className="creq-pageWrap">
            <div className="creq-modal creq-modal--wide creq-modal--page">
                {/* A booking needs a property (2026-08-04): a fresh account
                    gets pointed at the first-property flow, not an empty form. */}
                {propertiesLoaded && properties.length === 0 && pendingPropertyRequests.length === 0 ? (
                    <EmptyState
                        icon={faBuilding}
                        title="No properties added yet"
                        text="First add your property — then you can request a cleaning for it in seconds."
                    >
                        <button
                            type="button"
                            className="creq-btn creq-btn--primary"
                            style={{ marginTop: 14 }}
                            onClick={() => navigate('/client/properties?add=1')}
                        >
                            <FontAwesomeIcon icon={faPlus} /> Add your first property
                        </button>
                    </EmptyState>
                ) : done ? (
                    <div className="creq-success">
                        <div className="creq-pending-badge">
                            <FontAwesomeIcon icon={faHourglassHalf} />
                        </div>
                        <span className="creq-pending-chip">Pending review</span>
                        <h3>Not confirmed yet</h3>
                        <p>
                            {done === 'with_deposit'
                                ? 'Your booking request and deposit have been received. It is not confirmed yet — our team needs to review and approve the deposit first. We’ll notify you the moment it’s confirmed.'
                                : done === 'card'
                                    ? 'Your payment went through and your booking request has been received. It is not confirmed yet — it is now awaiting our team’s approval, and we’ll notify you the moment it’s approved.'
                                    : 'Your booking request has been received and is now awaiting our team’s approval. It is not confirmed yet — we’ll notify you the moment it’s approved.'}
                        </p>
                        {(notice?.messages?.last_minute || notice?.messages?.office_hours) && (
                            <div className="creq-notice creq-notice--done" role="note">
                                {notice.messages.last_minute && (
                                    <p className="creq-notice__line creq-notice__line--lm">{notice.messages.last_minute}</p>
                                )}
                                {notice.messages.office_hours && (
                                    <p className="creq-notice__line">{notice.messages.office_hours}</p>
                                )}
                            </div>
                        )}
                        {/* PAID BY CARD — what was taken, split the way it was
                            charged. No receipt links here: the receipts live on
                            the Wallet and Invoices pages, and this screen is
                            about the booking that is now awaiting approval. */}
                        {done === 'card' && cardPaid && (
                            <div className="creq-paid">
                                <div className="creq-paid__total">
                                    <span>Paid by card</span>
                                    <strong>{money(cardPaid.total)}</strong>
                                </div>
                                {cardPaid.invoices > 0 && (
                                    <>
                                        <div className="creq-paid__row">
                                            <span>This booking</span>
                                            <span>{money(cardPaid.booking)}</span>
                                        </div>
                                        <div className="creq-paid__row">
                                            <span>Pending invoices</span>
                                            <span>{money(cardPaid.invoices)}</span>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                        {done === 'autocharge' && autochargeCard && (
                            <p className="creq-pay-covered-note">
                                When the cleaning is completed, the final amount will be charged automatically to your
                                saved {autochargeCard.scheme ? `${autochargeCard.scheme} ` : ''}card ending {cardLast4(autochargeCard.masked_pan)}.
                            </p>
                        )}
                        {walletCoveredNote && (
                            <p className="creq-pay-covered-note">
                                Good news — your wallet balance covered the full amount, so no card payment was needed.
                            </p>
                        )}
                        <button className="creq-btn creq-btn--primary" onClick={() => navigate('/client/bookings')}>Done</button>
                    </div>
                ) : (
                    <>
                        <div className="creq-modal-header creq-page-header">
                            <div>
                                <button
                                    type="button"
                                    className="creq-page-back"
                                    onClick={exitToBookings}
                                    disabled={submitting}
                                >
                                    <FontAwesomeIcon icon={faArrowLeft} /> My Bookings
                                </button>
                                <h3>Request a Booking</h3>
                                <div className="creq-modal-sub">
                                    Pick a property, service and time — our team will confirm your booking.
                                </div>
                            </div>
                        </div>

                        {/* HIDDEN, never unmounted, while the card form is up:
                            the booking the client filled in is the thing being
                            paid for, and re-mounting it on Back would reset
                            every field and re-run the quote. */}
                        <div className="creq-modal-body" style={payStep === 'card' ? { display: 'none' } : undefined}>
                            {/* ── Property ── */}
                            <div className="creq-section-label">Property</div>
                            <Select
                                options={propertyOptions}
                                value={propertyOptions.find((o) => (
                                    form.pending_property_request_id
                                        ? o.value === `pr-${form.pending_property_request_id}`
                                        : o.value === form.property_id
                                )) || null}
                                placeholder="Select your property"
                                onChange={(opt) => {
                                    const v = opt ? opt.value : null;
                                    if (typeof v === 'string' && v.startsWith('pr-')) {
                                        // Pending property: All Inclusive only, priced
                                        // from the request's own snapshot.
                                        set({ property_id: null, pending_property_request_id: Number(v.slice(3)), type_id: 1 });
                                    } else {
                                        set({ property_id: v, pending_property_request_id: null });
                                    }
                                }}
                                styles={coralSelectStyles}
                                theme={coralSelectTheme}
                                menuPortalTarget={document.body}
                            />

                            {/* ── Service ── */}
                            <div className="creq-section-label">Service</div>
                            <div className="creq-type-grid">
                                {SERVICE_TYPES.map((t) => (
                                    <button
                                        key={t.id}
                                        type="button"
                                        className={`creq-segment creq-segment--type ${form.type_id === t.id ? 'is-active' : ''} ${form.pending_property_request_id && t.id !== 1 ? 'is-disabled' : ''}`}
                                        disabled={!!form.pending_property_request_id && t.id !== 1}
                                        title={form.pending_property_request_id && t.id !== 1 ? 'Available once the property is approved' : undefined}
                                        onClick={() => set({ type_id: t.id })}
                                    >
                                        <span className="creq-service-icon" aria-hidden="true">
                                            <FontAwesomeIcon icon={faWandMagicSparkles} />
                                        </span>
                                        <span className="creq-service-text">
                                            <strong>{t.name}</strong>
                                            <span>{t.desc}</span>
                                        </span>
                                        {form.type_id === t.id && (
                                            <FontAwesomeIcon icon={faCheckCircle} className="creq-service-check" aria-hidden="true" />
                                        )}
                                    </button>
                                ))}
                            </div>

                            {LINEN_HIRE_TYPES.includes(form.type_id) && (
                                <div className="creq-linen-deposit-note">
                                    <FontAwesomeIcon icon={faInfoCircle} aria-hidden="true" />
                                    <span>{LINEN_HIRE_NOTE}</span>
                                </div>
                            )}

                            {/* The same included-set disclosure as the
                                add-property form (2026-08-04) — the
                                service card promises linen & amenities, this
                                is where the promise is itemised. */}
                            <IncludedSet
                                label="✓ What's included in your All Inclusive Service"
                                quantities={includedQuantities}
                            />

                            {/* ── Schedule ──
                                B6 (2026-07-19): date first, like the mobile
                                app — a one-tap chip strip instead of the popup
                                calendar, and the time slots stay hidden until a
                                date is picked. Picking a date resets the time
                                (mobile CreateBookingSheet parity: the slots and
                                their lead-time availability belong to the day). */}
                            <BookingDateStrip
                                label={<span className="creq-section-label creq-datestrip-label">Date &amp; time</span>}
                                value={form.booking_date}
                                onSelect={(ds) => {
                                    if (ds !== form.booking_date) set({ booking_date: ds, booking_time: '' });
                                }}
                                disabled={submitting}
                            />

                            {isHourly && (
                                <div className="creq-grid">
                                    <label className="creq-label">
                                        Duration
                                        <Select
                                            options={DURATION_OPTIONS}
                                            value={DURATION_OPTIONS.find((o) => o.value === form.duration_hours) || null}
                                            onChange={(opt) => set({ duration_hours: opt ? opt.value : 2 })}
                                            styles={coralSelectStyles}
                                            theme={coralSelectTheme}
                                            menuPortalTarget={document.body}
                                            isSearchable={false}
                                        />
                                    </label>
                                </div>
                            )}

                            {/* Time: hidden until a date is chosen (B6), then all
                                slots as one-tap chips (too-soon greyed out). */}
                            {!form.booking_date ? (
                                <div className="creq-timegroup-note creq-timegroup-note--pickdate">
                                    Pick a date to choose a start time.
                                </div>
                            ) : (
                                <div className="creq-label creq-label--wide creq-timefield">
                                    <span className="creq-timefield-label">
                                        Start time
                                        {form.type_id === 1 && (
                                            <span className="creq-timegroup-note" style={{ marginLeft: 8 }}>10% off from 16:00</span>
                                        )}
                                    </span>
                                    <div className="creq-timechips" role="radiogroup" aria-label="Start time">
                                        {ALL_TIME_SLOTS.map((t) => {
                                            const active = form.booking_time === t;
                                            const offpeak = form.type_id === 1 && t >= OFFPEAK_FROM;
                                            const tooSoon = isSlotTooSoon(t);
                                            return (
                                                <button
                                                    key={t}
                                                    type="button"
                                                    role="radio"
                                                    aria-checked={active}
                                                    disabled={tooSoon}
                                                    title={tooSoon ? 'Bookings need at least 1.5 hours’ notice' : undefined}
                                                    className={`creq-timechip${active ? ' is-active' : ''}${offpeak ? ' is-offpeak' : ''}`}
                                                    onClick={() => set({ booking_time: t })}
                                                >
                                                    {t}
                                                    {offpeak && <span className="creq-timechip-dot" aria-hidden="true" />}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    {ALL_TIME_SLOTS.some((t) => isSlotTooSoon(t)) && (
                                        <div className="creq-timegroup-note creq-timegroup-note--lead">
                                            Bookings need at least 1.5 hours’ notice (Dubai time) — earlier slots are unavailable.
                                        </div>
                                    )}
                                </div>
                            )}

                            {form.booking_date && form.booking_time && (
                                <div className="creq-dt-confirm">
                                    <span className="creq-dt-confirm-dot" aria-hidden="true" />
                                    Booking for <strong>{readableWhen(form.booking_date, form.booking_time)}</strong>
                                </div>
                            )}

                            {/* Shown as soon as the slot is priced, well before the
                                Pay button — never a surprise at checkout. */}
                            {form.booking_date && form.booking_time && !quoteLoading
                                && (notice?.messages?.last_minute || notice?.messages?.office_hours) && (
                                <div className="creq-notice" role="note">
                                    {notice.messages.last_minute && (
                                        <p className="creq-notice__line creq-notice__line--lm">{notice.messages.last_minute}</p>
                                    )}
                                    {notice.messages.office_hours && (
                                        <p className="creq-notice__line">{notice.messages.office_hours}</p>
                                    )}
                                </div>
                            )}

                            {isHourly && (
                                <div className="creq-cleaners-row">
                                    <div className="creq-cleaners-label">How many cleaners?</div>
                                    <div className="creq-segmented creq-segmented--4">
                                        {[1, 2, 3, 4].map((n) => (
                                            <button
                                                key={n}
                                                type="button"
                                                className={`creq-segment ${form.required_cleaners === n ? 'is-active' : ''}`}
                                                onClick={() => set({ required_cleaners: n })}
                                            >
                                                <strong>{n}</strong>
                                                <span>{n === 1 ? 'cleaner' : 'cleaners'}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* ── Tasks for this booking (above Notes — 2026-07-15) ── */}
                            <div className="creq-section-label">Tasks for this cleaning (optional)</div>
                            <div className="creq-photos-note">
                                One-off tasks just for this booking — separate from your standing property and
                                all-units tasks.
                            </div>
                            <TaskListEditor
                                tasks={tasks}
                                onChange={setTasks}
                                addLabel="Add a task for this cleaning"
                                emptyHint="No extra tasks for this booking."
                                disabled={submitting}
                            />

                            {/* ── Extra Linens (optional) ── */}
                            {clientAddons.length > 0 && (
                                <>
                                    <div className="creq-section-label">Extra Linens (optional)</div>
                                    <div className="creq-section-hint">
                                        If you need extra linen-hire items for this booking only, select them here —
                                        these are additional items on top of your package.
                                    </div>
                                    <div className="creq-linen">
                                        {/* Only SELECTED linens are shown as rows — keeps the
                                            list short and controllable. Add more from the picker. */}
                                        {form.selected_addons.map((sa) => {
                                            const item = clientAddons.find((a) => a.id === Number(sa.item_id));
                                            if (!item) return null;
                                            return (
                                                <div className="creq-linen-row" key={sa.item_id}>
                                                    <span className="creq-linen-name">{item.name}</span>
                                                    <div className="creq-addon-stepper">
                                                        <button type="button" className="creq-addon-btn" aria-label={`Remove one ${item.name}`}
                                                            disabled={submitting} onClick={() => setAddonQty(item.id, sa.quantity - 1)}>−</button>
                                                        <span className="creq-addon-qty">{sa.quantity}</span>
                                                        <button type="button" className="creq-addon-btn" aria-label={`Add one ${item.name}`}
                                                            disabled={submitting || sa.quantity >= 20} onClick={() => setAddonQty(item.id, sa.quantity + 1)}>+</button>
                                                    </div>
                                                    <span className="creq-linen-line">{money(item.default_price * sa.quantity)}<span className="creq-addon-vat"> + VAT</span></span>
                                                    <button type="button" className="creq-linen-remove" aria-label={`Remove ${item.name}`}
                                                        disabled={submitting} onClick={() => setAddonQty(item.id, 0)}>×</button>
                                                </div>
                                            );
                                        })}
                                        <Select
                                            className="creq-linen-select"
                                            options={clientAddons
                                                .filter((a) => addonQty(a.id) === 0)
                                                .map((a) => ({ value: a.id, label: `${a.name} — ${money(a.default_price)} + VAT` }))}
                                            value={null}
                                            controlShouldRenderValue={false}
                                            isSearchable
                                            isDisabled={submitting || clientAddons.every((a) => addonQty(a.id) > 0)}
                                            placeholder="＋ Add extra linen…"
                                            noOptionsMessage={() => 'All extra linens added'}
                                            onChange={(opt) => { if (opt) setAddonQty(opt.value, addonQty(opt.value) + 1); }}
                                            styles={coralSelectStyles}
                                            theme={coralSelectTheme}
                                            menuPortalTarget={document.body}
                                        />
                                    </div>
                                </>
                            )}

                            {/* ── Notes ── */}
                            <div className="creq-section-label">Notes (optional)</div>
                            <label className="creq-label creq-label--wide">
                                <textarea
                                    maxLength={500}
                                    placeholder="Anything we should know for this booking…"
                                    value={form.notes}
                                    onChange={(e) => set({ notes: e.target.value })}
                                />
                            </label>

                            {/* ── Price + wallet panel ── */}
                            {quoteLoading ? (
                                <div className="creq-price-panel creq-price-loading">
                                    <span className="creq-price-spinner" aria-hidden="true" />
                                    Calculating price…
                                </div>
                            ) : quoteError ? (
                                <div className="creq-error">{quoteError}</div>
                            ) : quote ? (
                                <div className="creq-price-panel">
                                    <div className="creq-price-head">
                                        <div>
                                            <div className="creq-price-title">Estimated Price</div>
                                            <div className="creq-price-sub">Incl. VAT · final price confirmed on approval</div>
                                        </div>
                                        <div className="creq-price-total">{money(quote.estimate.total)}</div>
                                    </div>

                                    {quote.estimate.line_items.length > 0 && (
                                        <div className="creq-price-breakdown creq-price-breakdown--items">
                                            {quote.estimate.line_items.map((li, idx) => (
                                                <div className="creq-quote-line" key={`${li.item_id}-${idx}`}>
                                                    <div className="creq-quote-line-main">
                                                        <span className="creq-quote-line-name">
                                                            {li.quantity > 1 ? `${li.quantity} × ` : ''}{li.name}
                                                        </span>
                                                        <span className="creq-quote-line-amount">{money(li.line_subtotal)}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    <div className="creq-price-breakdown">
                                        <div className="creq-price-row">
                                            <span>Subtotal (excl. VAT)</span>
                                            <span>{money(quote.estimate.subtotal)}</span>
                                        </div>
                                        <div className="creq-price-row">
                                            <span>{quote.estimate.linen_deposit ? 'VAT (5% — deposit VAT-free)' : 'VAT (5%)'}</span>
                                            <span>{money(quote.estimate.vat)}</span>
                                        </div>
                                        <div className="creq-price-row creq-price-row--strong">
                                            <span>Total incl. VAT</span>
                                            <span>{money(quote.estimate.total)}</span>
                                        </div>
                                    </div>

                                    {quote.estimate.linen_deposit && (
                                        <div className="creq-linen-deposit-note">
                                            <FontAwesomeIcon icon={faInfoCircle} aria-hidden="true" />
                                            <span>{quote.estimate.linen_deposit.note || LINEN_DEPOSIT_NOTE}</span>
                                        </div>
                                    )}
                                </div>
                            ) : null}

                            {/* ── Payment ── */}
                            {quote && !quoteLoading && (
                                <>
                                    <div className="creq-section-label">Payment</div>
                                    {/* Pending-invoices fact (2026-07-17: settlement is
                                        mandatory at booking checkout, so no Invoices-page link —
                                        the card capture includes P and the wallet path settles P
                                        server-side). Shown in EVERY scenario (owner 2026-07-19:
                                        even with the pay-now breakdown's own "+ Pending invoices"
                                        row it was "not clear enough" that they get charged too), so
                                        it is no longer hidden in pay-now mode. Own bottom margin so
                                        it doesn't share a border with the payment card below it. */}
                                    {pendingInvoicesTotal > 0 && (
                                        <div className="creq-pending-invoices-notice">
                                            You have {money(pendingInvoicesTotal)} pending invoices — they will be added to your total charge.
                                        </div>
                                    )}
                                    <div className="creq-pay-grid">
                                        {/* ── ONE PAY-NOW PATH (2026-07-18) ──
                                            This was two separate option cards, "Use wallet" and "Pay
                                            now by card", each with its own summary of the same money.
                                            They were never really alternatives: with enough balance
                                            only one worked, and with a partial balance the client had
                                            to pick "card" to discover the wallet toggle that made it a
                                            split. Now there is one path and one toggle, and the rail is
                                            derived (payNow.js). The wallet detail rows are kept as
                                            secondary disclosure — they answer "why is my available
                                            lower than my balance", which the client still needs. */}
                                        <div className={`creq-pay-choice ${payMode === 'paynow' ? 'is-active' : ''}`}>
                                            {/* Selectable now that auto-payments can be
                                                pre-selected for signed-up clients: they must
                                                always be able to come back and pay for this
                                                one booking themselves. */}
                                            {payMode !== 'paynow' && (
                                                <button
                                                    type="button"
                                                    className="creq-segment creq-segment--type creq-pay-option"
                                                    onClick={() => {
                                                        payModeTouched.current = true;
                                                        setPayMode('paynow');
                                                        setError('');
                                                    }}
                                                    disabled={submitting}
                                                    aria-pressed={false}
                                                >
                                                    <span className="creq-service-icon creq-pay-icon" aria-hidden="true">
                                                        <FontAwesomeIcon icon={faCheckCircle} />
                                                    </span>
                                                    <span className="creq-service-text">
                                                        <strong>Pay now instead</strong>
                                                        <span>Pay for this booking yourself — wallet, card or both.</span>
                                                    </span>
                                                </button>
                                            )}
                                            {payMode === 'paynow' && (
                                            <div className="creq-pay-details creq-pay-details--wallet">
                                                {/* THE SINGLE DECISION. Hidden entirely when there is
                                                    nothing available — an unusable toggle is noise. */}
                                                {payPlan.hasWalletMoney && (
                                                    <label className="creq-pay-toggle">
                                                        <input
                                                            type="checkbox"
                                                            checked={useWalletBalance}
                                                            onChange={(e) => setUseWalletBalance(e.target.checked)}
                                                            disabled={submitting}
                                                        />
                                                        <span>Use my wallet balance ({money(availableRaw)})</span>
                                                    </label>
                                                )}

                                                <div className="creq-pay-breakdown">
                                                    {/* A4 (2026-07-19): the sum reads
                                                          + Booking amount
                                                          + Pending invoices
                                                          − Wallet amount
                                                          = whatever left to pay
                                                        — additions FIRST, then ONE wallet deduction
                                                        against the combined total, then one left-to-pay
                                                        line. The wallet line used to land before the
                                                        pending-invoices line; never reorder it back. */}
                                                    <div className="creq-price-row">
                                                        <span>This booking</span>
                                                        <span>{money(quote.estimate.total)}</span>
                                                    </div>
                                                    {/* Mandatory settlement — stated as a fact, no
                                                        opt-out (2026-07-17), ADDED into the total
                                                        before any deduction. Which rail settles it
                                                        (card leg, or the wallet on a fully-covered
                                                        booking) is the server's arithmetic, not a
                                                        display fork. */}
                                                    {pendingInvoicesTotal > 0 && (
                                                        <div className="creq-price-row">
                                                            <span>Pending invoices (settled with this payment)</span>
                                                            <span>+{money(pendingInvoicesTotal)}</span>
                                                        </div>
                                                    )}
                                                    {/* Per-entity split of the pending line (multi-
                                                        billing clients only — null for everyone else,
                                                        so nothing extra renders). */}
                                                    {pendingInvoicesTotal > 0 && pendingEntityBreakdown
                                                        && pendingEntityBreakdown.map((e, i) => (
                                                            <div key={i} className="creq-price-row creq-price-row--entity">
                                                                <span>{e.label}</span>
                                                                <span>{money(e.amount)}</span>
                                                            </div>
                                                        ))}
                                                    {/* ONE wallet deduction, against the combined total
                                                        (combinedCheckoutFigures), CLAMPED to the money
                                                        actually in the wallet: it includes P only when
                                                        the wallet covers booking + P (the wallet rail,
                                                        where the server settles P from the wallet too).
                                                        Anything less and the deduction is exactly the
                                                        wallet leg the server will debit. */}
                                                    {combined.walletDeduction > 0.005 && (
                                                        <div className="creq-price-row creq-wallet-reserved">
                                                            <span>From your wallet</span>
                                                            <span>−{money(combined.walletDeduction)}</span>
                                                        </div>
                                                    )}
                                                    {/* combined.needsCard, never payPlan.needsCard: the
                                                        card leg exists whenever ANYTHING is left of the
                                                        combined total — including P alone when the
                                                        wallet covers just the booking. This line may
                                                        never read 0.00 while a card capture is coming. */}
                                                    <div className="creq-price-row creq-price-row--strong">
                                                        <span>{combined.needsCard ? 'Left to pay by card' : 'Left to pay'}</span>
                                                        <span>{money(combined.leftToPay)}</span>
                                                    </div>
                                                    {/* NO "Wallet balance after" row here (owner
                                                        2026-07-19, twice: "Balance after still visible
                                                        on web view for booking request" — after already
                                                        ruling the balance lines under the breakdown
                                                        unnecessary). This surface's breakdown is EXACTLY
                                                        his formula and nothing else:
                                                        + booking + pending invoices − wallet = left to
                                                        pay. Do not re-add balance rows. */}
                                                </div>

                                                {/* Explanatory note about THIS split only — a pure
                                                    card leg renders no note at all. The secure-checkout
                                                    sentence is gone from this surface entirely (owner
                                                    2026-07-19: it had landed under the action buttons,
                                                    "why need to add this here… i dont know really");
                                                    the reassurance's one sanctioned home is
                                                    PayNowPanel, which this page does not host. */}
                                                {(cardSettlesPending || !combined.needsCard) && (
                                                    <div className="creq-pay-note">
                                                        {cardSettlesPending
                                                            ? (cardPortion > 0
                                                                ? `${money(pendingInvoicesTotal)} settles your pending invoices · ${money(cardPortion)} pays for this booking — one secure payment, recorded separately.`
                                                                : `${money(pendingInvoicesTotal)} settles your pending invoices — your wallet balance covers the booking itself. One secure payment, recorded separately.`)
                                                            : combined.walletSettlesPending
                                                                ? 'Your pending invoices are settled from your wallet together with this booking — no card payment needed.'
                                                                : 'Your wallet balance covers this booking — submitting will simply use your wallet, no card payment needed.'}
                                                    </div>
                                                )}

                                                {/* Card picker only when a card leg actually exists. */}
                                                {(cardPortion > 0 || cardSettlesPending) && !quote.covered_by_pending_deposits && (
                                                    <div className="creq-pay-cardpicker">
                                                        {/* One-off card payment only — saving a card lives in the
                                                            "Save card & pay automatically" option below (allowSaveNew
                                                            is off so no save checkbox appears here). */}
                                                        <SavedCardPicker
                                                            cards={savedCards}
                                                            value={cardSelection}
                                                            onChange={setCardSelection}
                                                            allowSaveNew={false}
                                                            disabled={submitting || savedCardBusy}
                                                            /* The security code belongs in the row of the
                                                               card it protects — the picker renders it
                                                               only for the card actually selected. */
                                                            cvvValue={savedCardActive ? savedCvv : null}
                                                            onCvvChange={savedCardActive ? setSavedCvv : null}
                                                            newCardSub={embeddedCardRail
                                                                ? 'Continue to input card details or select digital payments.'
                                                                : null}
                                                        />
                                                        {/* PAY WITH THE STORED CARD, HERE. No second
                                                            step and no redirect: the code above is all
                                                            that is missing, and the button below is
                                                            portalled into this page's action row so the
                                                            client presses the same button they would
                                                            have pressed to submit. */}
                                                        {savedCardActive && (
                                                            <SavedCardPay
                                                                card={selectedSavedCard}
                                                                compact
                                                                cvv={savedCvv}
                                                                onCvvChange={setSavedCvv}
                                                                onBusyChange={setSavedCardBusy}
                                                                actionSlot={paySlot}
                                                                payLabel={`Pay ${money(combined.leftToPay)}`}
                                                                disabled={submitting || !canSubmit || !pendingCheckDone}
                                                                charge={chargeSavedCardNow}
                                                                onPaid={onCardPaid}
                                                                onFailed={() => { /* the row states its own outcome */ }}
                                                            />
                                                        )}
                                                    </div>
                                                )}

                                                {/* Quiet nudge, not a third rail and NOT a second button
                                                    (2026-07-19). Same wording and same subordinate styling as
                                                    PayNowPanel's .pnp__nudge and mobile's <TopUpNudge>.
                                                    One wording for both wallet states on purpose: the old
                                                    empty-wallet variant led with "Earn bonuses", pitching
                                                    the wallet ahead of paying. Opening with "Or" keeps it
                                                    an aside. Only shown when topping up would actually
                                                    change the outcome — i.e. whenever a card leg
                                                    exists on the COMBINED total (cardLegExists, mobile
                                                    parity: checkoutPlan.showTopUpNudge measures the
                                                    shortfall against booking + P too). */}
                                                {cardLegExists && (
                                                    <button
                                                        type="button"
                                                        className="creq-pay-topupLink"
                                                        onClick={() => openDeposit()}
                                                        disabled={submitting}
                                                    >
                                                        Or top up your wallet and earn bonuses →
                                                    </button>
                                                )}

                                                {/* A SECOND standalone wallet-math block ("Wallet
                                                    balance / Other pending requests / Pending top-ups /
                                                    Balance if all confirmed…") rendered here until
                                                    2026-07-19. DELETED (item 13, owner screenshot): the
                                                    pay breakdown above already states the cost, the
                                                    wallet portion, the card charge and the balance
                                                    after off the ONE derived plan — the same fact
                                                    rendered twice with different arithmetic is
                                                    "absolutely confusing". One fact, one rendering.
                                                    Do not reintroduce a parallel arithmetic path. */}

                                                {quote.covered_by_pending_deposits && (
                                                    <div className="creq-wallet-covered">
                                                        <FontAwesomeIcon icon={faCheckCircle} aria-hidden="true" />
                                                        <span>
                                                            Covered by your pending top-up{pendingTopupsCount === 1 ? '' : 's'} — this
                                                            booking will be confirmed once your top-up is approved.
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                            )}
                                        </div>

                                        {/* ── NOT SIGNED UP → A QUIET INVITATION, NOT AN OPTION ──
                                            Owner, 2026-07-18: "If it's toggled off they should NEVER
                                            be auto charged — it should just show something like
                                            'Signup for simple auto payment at booking completion',
                                            even at checkout."
                                            Clicking this expands the sign-up copy; only the explicit
                                            confirm button enrols them and selects the rail. Nothing
                                            here is pre-selected and nothing here can submit. */}
                                        {/* The sign-up invitation is ITSELF auto-payment UI, so it is
                                            gated on AUTO_CHARGE_UI_ENABLED too — otherwise flipping the
                                            master switch off (autochargeAvailable=false) would leave the
                                            "!autochargeAvailable" invite showing, which is the opposite
                                            of hiding the feature (2026-07-20). */}
                                        {AUTO_CHARGE_UI_ENABLED && !autochargeAvailable && (
                                            <div className="creq-pay-invite">
                                                <button
                                                    type="button"
                                                    className="creq-pay-invite-head"
                                                    onClick={() => setAutoPayInviteOpen((v) => !v)}
                                                    disabled={submitting}
                                                    aria-expanded={autoPayInviteOpen}
                                                >
                                                    <FontAwesomeIcon icon={faShieldHalved} aria-hidden="true" />
                                                    <span>{AUTO_PAY_INVITE}</span>
                                                </button>
                                                {autoPayInviteOpen && (
                                                    <div className="creq-pay-invite-body">
                                                        <p className="creq-pay-invite-copy">{AUTO_PAY_BLURB}</p>
                                                        <p className="creq-pay-invite-copy creq-pay-invite-copy--muted">
                                                            You can switch this off anytime in Account → Payment methods.
                                                            Until you switch it on, nothing is ever charged automatically.
                                                        </p>
                                                        <button
                                                            type="button"
                                                            className="creq-pay-invite-cta"
                                                            onClick={signUpForAutoPay}
                                                            disabled={autoPaySigningUp || submitting}
                                                        >
                                                            {autoPaySigningUp ? 'Switching on…' : 'Turn on automatic payments'}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Signed-up clients only — the auto-charge rail as a real,
                                            pre-selected option. With saved cards → pick which one
                                            auto-charges; without → the save-a-card consent path. */}
                                        {autochargeAvailable && (
                                            <div className={`creq-pay-choice ${payMode === 'autocharge' ? 'is-active' : ''}`}>
                                                <button
                                                    type="button"
                                                    className={`creq-segment creq-segment--type creq-pay-option ${payMode === 'autocharge' ? 'is-active' : ''}`}
                                                    onClick={() => { payModeTouched.current = true; setPayMode('autocharge'); setError(''); }}
                                                    disabled={submitting}
                                                    aria-pressed={payMode === 'autocharge'}
                                                >
                                                    <span className="creq-service-icon creq-pay-icon" aria-hidden="true">
                                                        <FontAwesomeIcon icon={faShieldHalved} />
                                                    </span>
                                                    {/* P4 (2026-07-19): "If someone already signed
                                                        up for auto payments, no need to still display all
                                                        those notes — we already verified with them during
                                                        sign up. At checkout we can just have a shorter
                                                        display like 'Auto Payment · Booking Completion'."

                                                        Short form is for the client who is BOTH signed up
                                                        and has a card — the pair that means everything was
                                                        already explained and agreed. Signed up WITHOUT a
                                                        card still gets the full copy below, because that
                                                        client is about to save a card here and has not
                                                        seen the storage terms yet. */}
                                                    <span className="creq-service-text">
                                                        {hasSavedCard ? (
                                                            <>
                                                                <strong>Auto Payment · Booking Completion</strong>
                                                                <span>
                                                                    {autochargeCard
                                                                        ? `Charged to •••• ${cardLast4(autochargeCard.masked_pan)} after each cleaning.`
                                                                        : 'Charged to your saved card after each cleaning.'}
                                                                </span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <strong>Save card &amp; pay automatically</strong>
                                                                <span>Save your card securely — we charge it only when each cleaning is completed.</span>
                                                            </>
                                                        )}
                                                    </span>
                                                    {payMode === 'autocharge' && (
                                                        <FontAwesomeIcon icon={faCheckCircle} className="creq-service-check" aria-hidden="true" />
                                                    )}
                                                </button>

                                                {payMode === 'autocharge' && (
                                                    <div className="creq-pay-details">
                                                        {/* NO CARD YET → the AED 1.00 sign-up HOLD (released
                                                            automatically — never captured, never a wallet
                                                            row). It states the booking total explicitly as
                                                            the figure that is NOT taken (2026-07-18),
                                                            then names the temporary hold. Both numbers are
                                                            visible so neither can be mistaken for the
                                                            other. */}
                                                        {!hasSavedCard && (
                                                            <>
                                                                <div className="creq-pay-note creq-pay-note--strong">
                                                                    {autoPaySetupHeadline(money(quote ? quote.estimate.total : 0))}
                                                                </div>
                                                                <div className="creq-pay-note creq-pay-note--tight">
                                                                    {AUTOPAY_SETUP_BODY}
                                                                </div>
                                                                <div className="creq-pay-rows creq-pay-rows--autopay">
                                                                    <div className="creq-pay-row creq-pay-row--muted">
                                                                        <span>{AUTOPAY_SETUP_ROW_BOOKING}</span>
                                                                        <span>{money(quote ? quote.estimate.total : 0)}</span>
                                                                    </div>
                                                                    <div className="creq-pay-row creq-pay-row--due">
                                                                        <span>{AUTOPAY_SETUP_ROW_VERIFICATION}</span>
                                                                        {/* Bound to the LITERAL, never to a
                                                                            booking figure. */}
                                                                        <span>{AUTOPAY_SETUP_AMOUNT_LABEL}</span>
                                                                    </div>
                                                                </div>
                                                                {pendingInvoicesTotal > 0 && (
                                                                    /* Stated, never silently rolled into the
                                                                       AED 1.00 hold — see autoPaySetupCopy.js. */
                                                                    <div className="creq-pay-note creq-pay-note--tight">
                                                                        {autoPaySetupPendingNote(money(pendingInvoicesTotal))}
                                                                    </div>
                                                                )}
                                                            </>
                                                        )}

                                                        {hasSavedCard ? (
                                                            savedCards.length > 1 ? (
                                                                <div className="creq-pay-cardpicker">
                                                                    <div className="hcard-picker">
                                                                        {savedCards.map((card) => {
                                                                            const active = autochargeCardId === card.id;
                                                                            return (
                                                                                <button
                                                                                    key={card.id}
                                                                                    type="button"
                                                                                    className={`hcard-option ${active ? 'is-active' : ''}`}
                                                                                    onClick={() => setAutochargeCardId(card.id)}
                                                                                    disabled={submitting}
                                                                                    aria-pressed={active}
                                                                                >
                                                                                    <span className="hcard-radio" aria-hidden="true" />
                                                                                    <span className="hcard-option-main">
                                                                                        <span className="hcard-option-label">
                                                                                            <SchemeBadge scheme={card.scheme} />
                                                                                            <span>•••• {cardLast4(card.masked_pan)}</span>
                                                                                        </span>
                                                                                        <span className="hcard-option-sub">
                                                                                            Saved card
                                                                                        </span>
                                                                                    </span>
                                                                                </button>
                                                                            );
                                                                        })}
                                                                    </div>
                                                                </div>
                                                            ) : autochargeCard ? (
                                                                <div className="creq-pay-cardline">
                                                                    <SchemeBadge scheme={autochargeCard.scheme} />
                                                                    <span>•••• {cardLast4(autochargeCard.masked_pan)}</span>
                                                                    {/* Card expiry is never shown to clients (owner
                                                                        2026-07-19): scheme + last4 is enough. */}
                                                                </div>
                                                            ) : null
                                                        ) : (
                                                            /* No card on file yet → the save-a-card path. Ticking the
                                                               consent (exact CARD_CONSENT_TEXT) is required to submit. */
                                                            <div className="creq-pay-savecard">
                                                                <div className="creq-pay-note creq-pay-note--tight">
                                                                    No card saved yet — you&apos;ll add it securely on the next page.
                                                                </div>
                                                                <label className="hcard-save">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={autochargeConsent}
                                                                        onChange={(e) => setAutochargeConsent(e.target.checked)}
                                                                        disabled={submitting}
                                                                    />
                                                                    <span>Save my card securely for automatic payments</span>
                                                                </label>
                                                                <div className="hcard-consent hcard-consent--readonly">
                                                                    <span className="hcard-consent-text">{CARD_CONSENT_TEXT}</span>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Security reassurance — for the client who is about
                                                            to hand over a card. An enrolled client with a
                                                            card already read this at sign-up; repeating it
                                                            every checkout is the clutter P4 removes. */}
                                                        {!hasSavedCard && (
                                                        <div className="creq-pay-secure">
                                                            <FontAwesomeIcon icon={faShieldHalved} aria-hidden="true" />
                                                            <span>
                                                                <strong>Stored securely by Network International (N-Genius)</strong> — we never see or store your card number.
                                                            </span>
                                                        </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                </>
                            )}

                            {!!payNotice && <div className="creq-notice">{payNotice}</div>}

                            {/* ZERO FLOOR (P3). The server refused because this
                                spend would take AVAILABLE below zero. Rendered
                                as a shortfall panel with the three resolving
                                actions — never a dead error. "Pay by card" is
                                the same Pay-now path with the wallet toggle
                                already applied, so the card leg is the natural
                                continuation rather than a separate rail. */}
                            {walletFloor && (
                                <WalletShortfallPanel
                                    cost={walletFloor.required != null
                                        ? walletFloor.required
                                        : (quote ? Number(quote.estimate.total) : 0)}
                                    available={walletFloor.available != null
                                        ? walletFloor.available
                                        : availableRaw}
                                    totalBalance={walletFloor.totalBalance}
                                    committed={walletFloor.committed}
                                    amountOwed={walletFloor.amountOwed}
                                    shortfall={walletFloor.shortfall}
                                    disabled={submitting}
                                    onPayByCard={() => {
                                        setWalletFloor(null);
                                        setUseWalletBalance(true);
                                        submitCardCheckout();
                                    }}
                                    onTopUp={() => { setWalletFloor(null); openDeposit(); }}
                                    onReduce={() => {
                                        setWalletFloor(null);
                                        // A page scrolls with the window, not an inner body.
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }}
                                />
                            )}

                            {!!error && <div className="creq-error">{error}</div>}
                            {adjustmentsDue && (
                                <div className="creq-error creq-adjDue">
                                    <div className="creq-adjDue__text">
                                        {adjustmentsDue.message}
                                        {adjustmentsDue.total > 0 && (
                                            <> Total due: <strong>{money(adjustmentsDue.total)}</strong>.</>
                                        )}
                                    </div>
                                    <a className="creq-adjDue__link" href="/client/bookings">
                                        Go to My Bookings to review &amp; settle →
                                    </a>
                                </div>
                            )}
                        </div>

                        {/* STEP 2 — the card form on its own. The gateway's
                            fields are tall and the booking above is long; one
                            page holding both is more than anyone can take in at
                            the moment they are typing a card number. */}
                        {payStep === 'card' && (
                            <div className="creq-modal-body creq-cardStep">
                                {/* WHAT THIS CARD IS ABOUT TO PAY FOR, itemised.
                                    The same rows, labels and figures as the
                                    breakdown on the form — one arithmetic path,
                                    stated again at the moment the card is typed,
                                    because a client whose booking and pending
                                    invoices travel in ONE capture must be able to
                                    see both in the figure they are approving. */}
                                {quote && (
                                    <div className="creq-cardStep__summary">
                                        <div className="creq-cardStep__row">
                                            <span>This booking</span>
                                            <span>{money(quote.estimate.total)}</span>
                                        </div>
                                        {pendingInvoicesTotal > 0 && (
                                            <div className="creq-cardStep__row">
                                                <span>Pending invoices (settled with this payment)</span>
                                                <span>+{money(pendingInvoicesTotal)}</span>
                                            </div>
                                        )}
                                        {pendingInvoicesTotal > 0 && pendingEntityBreakdown
                                            && pendingEntityBreakdown.map((e, i) => (
                                                <div key={i} className="creq-cardStep__row creq-cardStep__row--entity">
                                                    <span>{e.label}</span>
                                                    <span>{money(e.amount)}</span>
                                                </div>
                                            ))}
                                        {combined.walletDeduction > 0.005 && (
                                            <div className="creq-cardStep__row creq-cardStep__row--wallet">
                                                <span>From your wallet</span>
                                                <span>−{money(combined.walletDeduction)}</span>
                                            </div>
                                        )}
                                        <div className="creq-cardStep__total">
                                            <span>To pay by card</span>
                                            <strong>{money(combined.leftToPay)}</strong>
                                        </div>
                                    </div>
                                )}
                                {cardSettlesPending && (
                                    <div className="creq-cardStep__note">
                                        Your booking and your pending invoices are paid in one secure payment, and
                                        recorded separately.
                                    </div>
                                )}
                                {!!error && <div className="creq-error">{error}</div>}
                                <HostedCardPanel
                                    /* Keyed on the amount: the SDK reads it once, at mount,
                                       and Apple draws its sheet from that figure — a changed
                                       total must build a NEW form, never re-label an old one. */
                                    key={`req-${combined.leftToPay.toFixed(2)}`}
                                    payLabel={`Pay ${money(combined.leftToPay)}`}
                                    amountAed={combined.leftToPay}
                                    disabled={submitting || !canSubmit}
                                    startPayment={startHostedPayment}
                                    onUnavailable={() => { setHostedRail('off'); setPayStep('form'); }}
                                    onPaid={onCardPaid}
                                    onFailed={() => { /* the panel states its own outcome and re-arms */ }}
                                    actionSlot={paySlot}
                                >
                                    {/* Keep this card — the same offer, wording
                                        and consent record as the wallet top-up
                                        and the invoice payment page. Saving here
                                        is a one-off card being stored, not
                                        enrolment in auto-charge, which remains
                                        its own deliberate option on the form. */}
                                    <label className="hcard-save">
                                        <input
                                            type="checkbox"
                                            checked={saveNewCard}
                                            onChange={(e) => setSaveNewCard(e.target.checked)}
                                        />
                                        <span>Save this card for future payments</span>
                                    </label>
                                    {saveNewCard && (
                                        <div className="hcard-consent hcard-consent--readonly">
                                            <span className="hcard-consent-text">
                                                <span className="hcard-consent-mandate">{CARD_CONSENT_TEXT}</span>
                                            </span>
                                        </div>
                                    )}
                                </HostedCardPanel>
                            </div>
                        )}

                        {/* LAST-MINUTE TICK. One place, directly above the one
                            action row every way of paying ends in. */}
                        {ackRequired && (
                            <label className="creq-lmack">
                                <input
                                    type="checkbox"
                                    checked={lmAck}
                                    disabled={submitting}
                                    onChange={(e) => { setLmAck(e.target.checked); if (e.target.checked) setError(''); }}
                                />
                                <span>{notice.messages.ack_label}</span>
                            </label>
                        )}

                        <div className="creq-modal-actions">
                            <button
                                className="creq-btn creq-btn--ghost"
                                onClick={payStep === 'card'
                                    ? () => { releaseStagedCheckout(); setPayStep('form'); setError(''); }
                                    : exitToBookings}
                                disabled={submitting}
                            >
                                {payStep === 'card' ? 'Back' : 'Cancel'}
                            </button>
                            {/* The embedded rails put their own Pay button here
                                (portalled), so this page always ends in one
                                action row whichever way the client is paying. */}
                            <div className="creq-paySlot" ref={setPaySlot} />
                            {!embeddedPayOwnsButton && (
                            <button
                                className="creq-btn creq-btn--primary"
                                onClick={handleSubmitClick}
                                disabled={submitting || !canSubmit || autochargeConsentBlocked || !pendingCheckDone}
                            >
                                {submitting
                                    ? 'Submitting…'
                                    : !pendingCheckDone
                                        ? 'Checking your account…'
                                    : quoteLoading
                                        ? 'Calculating…'
                                        : payMode === 'autocharge'
                                            /* No card yet → the sign-up rail, which captures
                                               NOTHING — only a temporary AED 1.00 hold that
                                               is released automatically. The label is a fixed
                                               string in autoPaySetupCopy.js, not a formatted
                                               amount, so it can never render a booking
                                               figure. With a card on file there is no hold
                                               either. */
                                            ? (hasSavedCard ? 'Submit — auto-charge on completion' : AUTOPAY_SETUP_BUTTON)
                                            /* ONE primary button. Its label states the ACTUAL
                                               outcome of the derived rail rather than naming a
                                               method the client had to pick. */
                                            /* Plan-derived, amountless, mobile-parity
                                               (payNowRailLabel ports checkoutPlan.ts's
                                               payNowLabel). The figure lives in the price
                                               summary above — one arithmetic path — and the
                                               label names only the rails this payment
                                               actually touches, so a hybrid split reads
                                               "Pay now by Card or Wallet" rather than
                                               naming the card leg alone. */
                                            : showPayNowPrimary
                                                ? payNowRailLabel(payPlan, { extras: cardSettlesPending ? pendingInvoicesTotal : 0 })
                                                : 'Submit Request'}
                            </button>
                            )}
                            {/* The secure-checkout line that sat here (under the
                                action buttons) is REMOVED (2026-07-19). The secure-page reassurance lives in
                                exactly ONE place per surface — PayNowPanel's
                                SECURE_CHECKOUT_NOTE where a card leg exists — and
                                this page renders nothing beneath its buttons. */}
                        </div>
                    </>
                )}

                {/* The top-up form allows the card rail (owner 2026-07-19 fix
                    kept from the modal era): a card top-up redirects to the
                    hosted paypage, and the half-filled booking survives because
                    openDeposit snapshots the draft to localStorage
                    (bookingRequestDraft) and ClientDepositForm takes a
                    returnPath — the paypage returns to
                    /client/payment-return/bookings, which offers "Continue my
                    booking request" and reopens THIS PAGE fully prefilled
                    (?resumeDraft=1). No money behaviour changes — it is the
                    same top-up checkout the wallet page has always used, and
                    the booking is still only submitted by an explicit submit
                    afterwards. */}
                {showDeposit && (
                    <ClientDepositForm
                        walletClientId={quote?.wallet_client_id || null}
                        walletLabel={quote?.wallet_label || null}
                        initialAmount={depositPrefill ?? shortfall}
                        onClose={() => { setShowDeposit(false); setDepositPrefill(null); }}
                        onSuccess={handleDepositSuccess}
                        allowCard
                        returnPath="/client/payment-return/bookings"
                    />
                )}
            </div>
        </div>
    );
}
